import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { EventChip, type ChipTone } from "./EventChip";
import MonthStrip from "./MonthStrip";

export interface CalendarEvent {
  id: string | number;
  date: string | Date;
  title: React.ReactNode;
  meta?: React.ReactNode;
  tone?: ChipTone;
  icon?: React.ReactNode;
  /** Route to open when the chip is clicked. */
  to?: string;
}

export interface CalendarLegendItem {
  label: string;
  tone: ChipTone;
  count: number;
}

interface ActivityCalendarProps {
  title?: string;
  events: CalendarEvent[];
  legend?: CalendarLegendItem[];
  /** Right-hand column content under the month grid, e.g. upcoming or recent items. */
  aside?: React.ReactNode;
  asideTitle?: string;
  actions?: React.ReactNode;
  className?: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const DOT: Record<ChipTone, string> = {
  lavender: "bg-pastel-lavender-ink",
  peach: "bg-pastel-peach-ink",
  rose: "bg-pastel-rose-ink",
  sky: "bg-pastel-sky-ink",
  mint: "bg-pastel-mint-ink",
  lilac: "bg-pastel-lilac-ink",
  muted: "bg-mute",
};

function toDate(value: string | Date) {
  if (value instanceof Date) return value;
  // Plain dates ("2026-02-09") are civil dates; read them in local time.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
}

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * Month-at-a-glance activity view: a pill month strip, one row per day with
 * pastel event chips, and a compact month grid with status dots beside it.
 */
export default function ActivityCalendar({
  title = "Activity calendar",
  events,
  legend,
  aside,
  asideTitle,
  actions,
  className,
}: ActivityCalendarProps) {
  const today = useMemo(() => new Date(), []);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const d = toDate(e.date);
      if (Number.isNaN(d.getTime())) continue;
      const key = dayKey(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return map;
  }, [events]);

  // Open on the month of the latest activity, or this month when there is none.
  const initial = useMemo(() => {
    const dates = events.map((e) => toDate(e.date)).filter((d) => !Number.isNaN(d.getTime()));
    const latest = dates.sort((a, b) => b.getTime() - a.getTime())[0];
    const base = latest && latest < today ? latest : today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  }, [events, today]);

  const [month, setMonth] = useState(initial);
  useEffect(() => setMonth(initial), [initial]);

  const year = month.getFullYear();
  const daysInMonth = new Date(year, month.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month.getMonth(), i + 1));
  const leading = (new Date(year, month.getMonth(), 1).getDay() + 6) % 7;

  const rowRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const listRef = useRef<HTMLOListElement>(null);
  const [focusDay, setFocusDay] = useState<number | null>(null);

  const jumpTo = (day: number) => {
    setFocusDay(day);
    const row = rowRefs.current[day];
    const list = listRef.current;
    if (row && list) list.scrollTo({ top: row.offsetTop - list.offsetTop, behavior: "smooth" });
  };

  const shiftMonth = (delta: number) => {
    setFocusDay(null);
    setMonth(new Date(year, month.getMonth() + delta, 1));
  };

  const isToday = (d: Date) => dayKey(d) === dayKey(today);

  // Bring the first day with activity (or today) into view when the month changes.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const target = days.find((d) => byDay.has(dayKey(d)) || isToday(d));
    const row = target ? rowRefs.current[target.getDate()] : null;
    list.scrollTo({ top: row ? Math.max(0, row.offsetTop - list.offsetTop - 8) : 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, byDay]);

  return (
    <section
      className={cn(
        "grid min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-soft xl:grid-cols-[minmax(0,1fr)_360px]",
        className,
      )}
    >
      <div className="flex min-w-0 flex-col">
        <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5 md:px-6">
          <h2 className="text-base font-medium text-foreground">{title}</h2>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
        <div className="border-b border-border px-4 py-3 md:px-5">
          <MonthStrip
            aria-label={`Months of ${year}`}
            options={MONTHS.map((label, i) => ({ value: String(i), label }))}
            value={String(month.getMonth())}
            onChange={(v) => {
              setFocusDay(null);
              setMonth(new Date(year, Number(v), 1));
            }}
          />
        </div>
        <ol ref={listRef} className="relative max-h-[640px] min-h-0 flex-1 overflow-y-auto" aria-label={`${MONTHS[month.getMonth()]} ${year}`}>
          {days.map((d) => {
            const dayEvents = byDay.get(dayKey(d)) ?? [];
            const weekend = d.getDay() === 0 || d.getDay() === 6;
            const highlight = isToday(d) || focusDay === d.getDate();
            return (
              <li
                key={d.getDate()}
                ref={(el) => {
                  rowRefs.current[d.getDate()] = el;
                }}
                className="flex min-h-[72px] items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 md:gap-4 md:px-5"
              >
                <span
                  className={cn(
                    "flex size-9 shrink-0 items-center justify-center rounded-full text-sm tabular-nums",
                    highlight ? "bg-primary font-medium text-primary-foreground" : "text-body",
                  )}
                  aria-label={d.toDateString()}
                >
                  {d.getDate()}
                </span>
                {dayEvents.length > 0 ? (
                  <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                    {dayEvents.map((e) =>
                      e.to ? (
                        <Link
                          key={e.id}
                          to={e.to as any}
                          className="max-w-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <EventChip
                            tone={e.tone}
                            icon={e.icon}
                            title={e.title}
                            meta={e.meta}
                            className="transition-[transform,box-shadow] hover:-translate-y-px hover:shadow-soft"
                          />
                        </Link>
                      ) : (
                        <EventChip key={e.id} tone={e.tone} icon={e.icon} title={e.title} meta={e.meta} />
                      ),
                    )}
                  </div>
                ) : (
                  <span className="text-[13px] text-muted-foreground">{weekend ? "Weekend" : "No events."}</span>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <aside className="flex min-w-0 flex-col border-t border-border xl:border-t-0 xl:border-l">
        <div className="border-b border-border px-5 py-5 md:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              aria-label="Previous month"
              className="flex size-8 items-center justify-center rounded-full border border-border text-body transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft className="size-4" />
            </button>
            <p className="min-w-0 flex-1 text-center text-[15px] font-medium text-foreground">
              {month.toLocaleString("default", { month: "long" })} {year}
            </p>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              aria-label="Next month"
              className="flex size-8 items-center justify-center rounded-full border border-border text-body transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-7 gap-y-1.5 text-center">
            {WEEKDAYS.map((w) => (
              <span key={w} className="pb-1 text-xs font-medium text-muted-foreground">
                {w}
              </span>
            ))}
            {Array.from({ length: leading }, (_, i) => (
              <span key={`pad-${i}`} />
            ))}
            {days.map((d) => {
              const dayEvents = byDay.get(dayKey(d)) ?? [];
              const weekend = d.getDay() === 0 || d.getDay() === 6;
              const active = isToday(d) || focusDay === d.getDate();
              const tones = Array.from(new Set(dayEvents.map((e) => e.tone ?? "lavender"))).slice(0, 3);
              return (
                <button
                  key={d.getDate()}
                  type="button"
                  onClick={() => jumpTo(d.getDate())}
                  aria-label={`${d.toDateString()}${dayEvents.length ? `, ${dayEvents.length} events` : ""}`}
                  className={cn(
                    "relative mx-auto flex size-9 flex-col items-center justify-center rounded-full text-[13px] tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active
                      ? "bg-primary font-medium text-primary-foreground"
                      : weekend
                        ? "text-muted-foreground hover:bg-muted"
                        : "bg-muted text-foreground hover:bg-accent",
                  )}
                >
                  {d.getDate()}
                  {tones.length > 0 && (
                    <span className="absolute bottom-1 flex gap-0.5" aria-hidden>
                      {tones.map((t) => (
                        <span key={t} className={cn("size-1 rounded-full", active ? "bg-primary-foreground" : DOT[t])} />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {legend && legend.length > 0 && (
            <ul className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
              {legend.map((l) => (
                <li key={l.label} className="flex items-center gap-1.5 text-xs text-body">
                  <span className={cn("size-1.5 rounded-full", DOT[l.tone])} aria-hidden />
                  {l.label}
                  <span className="rounded-full bg-muted px-1.5 text-[11px] leading-[18px] text-foreground tabular-nums">
                    {l.count}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {aside && (
          <div className="min-w-0 flex-1 px-5 py-5 md:px-6">
            {asideTitle && <h3 className="mb-4 text-base font-medium text-foreground">{asideTitle}</h3>}
            {aside}
          </div>
        )}
      </aside>
    </section>
  );
}

/** A tinted card for the calendar's side list ("Upcoming events" style). */
export function CalendarAsideCard({
  tone = "lavender",
  title,
  meta,
  badge,
  to,
}: {
  tone?: ChipTone;
  title: React.ReactNode;
  meta?: React.ReactNode;
  badge?: React.ReactNode;
  to?: string;
}) {
  const body = (
    <div
      className={cn(
        "rounded-2xl p-4 transition-[transform,box-shadow]",
        {
          lavender: "bg-pastel-lavender",
          peach: "bg-pastel-peach",
          rose: "bg-pastel-rose",
          sky: "bg-pastel-sky",
          mint: "bg-pastel-mint",
          lilac: "bg-pastel-lilac",
          muted: "bg-muted",
        }[tone],
        to && "hover:-translate-y-px hover:shadow-soft",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
          {meta && <p className="mt-0.5 truncate text-xs text-body">{meta}</p>}
        </div>
        {badge && (
          <span className="shrink-0 rounded-full bg-card px-2.5 py-1 text-[11px] font-medium text-foreground shadow-[0_1px_2px_rgb(0_0_0/0.05)]">
            {badge}
          </span>
        )}
      </div>
    </div>
  );
  return to ? (
    <Link to={to as any} className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {body}
    </Link>
  ) : (
    body
  );
}
